import { Platform } from "react-native";
import type { FontVariant } from "react-native";

// ============================================================================
// Theme Type — canonical interface for the entire color system
// ============================================================================

export interface Theme {
  // Background hierarchy
  backgroundVoid: string;
  backgroundRoot: string;
  backgroundElevated: string;
  surfaceCard: string;
  surfaceFloating: string;
  surfaceGlow: string;

  // Accent system
  primary: string;
  primaryBright: string;
  primaryGlow: string;
  accentCyan: string;
  accentCyanGlow: string;
  accentPurple: string;
  accentAmber: string;
  accentPink: string;

  // Semantic risks
  riskRed: string;
  riskOrange: string;
  riskYellow: string;
  riskGreen: string;

  // Text hierarchy
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textMuted: string;

  // Legacy aliases (still in use across the codebase)
  text: string;
  textDisabled?: string;
  buttonText: string;
  tabIconDefault: string;
  tabIconSelected: string;
  link: string;
  backgroundDefault: string;
  backgroundSecondary: string;
  backgroundTertiary: string;
  border: string;
  primaryBlue: string;
  accentBlue: string;
  accentElectric?: string;
  accentNeon?: string;
  glowCritical?: string;
  glowWarning?: string;
  glowCyan?: string;
  glowGreen?: string;
  surfaceElevated?: string;
  surfaceOverlay?: string;
  background: string;
  surface: string;
  borderActive?: string;
  borderCritical?: string;
  borderWarning?: string;
  focusBorder?: string;

  // DS v1.0 additions — surface/border hierarchy (MUST)
  surfaceDeep: string;     // DS surface-3 (deepest card nesting)
  rule: string;            // DS rule (hairline divider)
  ruleBold: string;        // DS rule-2 (stronger divider)
  hair: string;            // DS hair (card border hairline)

  // Ink-on-gold — text color for gold-filled buttons/badges
  inkOnGold: string;        // DS --ink-on-gold

  // DS gold accent variants
  goldSoft: string;        // DS gold-soft background tint
  goldLine: string;        // DS gold-line accent border

  // DS semantic soft tints (badge/signal backgrounds)
  bullSoft: string;        // DS bull-soft
  bearSoft: string;        // DS bear-soft
  warnSoft: string;        // DS warn-soft

  // Theme accent yellow (general-purpose warning/highlight)
  themeYellow: string;

  // ==========================================================================
  // Design dictionary (2026-08-04) — cream light palette + contrast contract
  // ==========================================================================
  //
  // ⚠️ CONTRAST CONTRACT — read before using any of these.
  //
  // 🔴 REBASED 2026-08-15 onto the founder's stated text hierarchy. Measured on
  // Cream (#F5F0E7), the theme's own `backgroundRoot` — NOT on Warm Sand, which
  // is a different surface and understates every token by ~0.35:
  //
  //     Obsidian     #171714  16.94:1  ✅ any size   — headers, data
  //     Slate        #504B43   7.75:1  ✅ any size   — body, descriptions
  //     Silver       #6B6459   4.02:1  ⚠️ FOUNDER FLOOR — meta labels
  //     Critical Red #943A32   4.45:1  ⚠️ FOUNDER FLOOR — status
  //     Contagion Bl #5C6C82   5.27:1  ✅ any size   — status
  //     Positive     #176448   7.08:1  ✅ any size
  //     Warning      #96700E   4.47:1  ⚠️ just under AA — riskYellow
  //     goldText     #8A5A12   5.93:1  ✅ any size
  //     Gold         #A97826   3.90:1  ❌❌ NEVER small text on cream
  //     Yield Gold   #A97826   2.52:1  ❌❌ FILL ONLY, at any size, anywhere
  //
  // ⚠️ SILVER AND CRITICAL RED SIT BELOW WCAG-AA (4.5) AND THAT IS DELIBERATE.
  // They are the founder's stated hierarchy, requested three times. Silver's
  // CEILING is 4.09:1 on pure white — it cannot reach AA on ANY light ground
  // this app could paint, so "darken it slightly" is not an available move; the
  // only alternative was not using the specified colour. The floor is therefore
  // 4.0, enforced with a negative control in designSystemContrast.test.ts so the
  // exception cannot widen into "anything goes".
  //
  // Consequence to state plainly rather than discover: the app's Lighthouse
  // accessibility score was 100 with the previous values (Muted 5.10, Danger
  // 5.99). Any small text in Silver or Critical Red can now be flagged.
  //
  // Each failing color still has a `*Text` sibling that is safe at any size —
  // that mechanism is unchanged and is what keeps the gold out of type. Use
  // `primary` for the RAIL and `goldText` for the NUMBER, or ink on a gold fill
  // via `inkOnGold`. The two founder-floor tokens above are the only sanctioned
  // sub-AA text values in the system; nothing else may join them without the
  // same explicit decision and its own negative control.
  goldText: string;   // gold, darkened to ≥4.5:1 — confidence scores, links
  bearText: string;   // danger — loss figures, errors
  steelBlue: string;  // information — contagion / neutral precedent — AA as-is
  silverFill: string; // mute-2 #A8A49A — rules/dots/icons, NEVER text
  redFill: string;    // deeper red #C24033 — fills/bars/dots, NEVER small text
  steelSoft: string;  // 12% information tint for badge grounds

  // Glassmorphism — the two surfaces in the system. Gradient stops are
  // ordered [start, end] on a 135° axis, matching the brief.
  glassFill1: string;
  glassFill2: string;
  // Edge lighting: one overhead source, so top/left catch it and right/bottom
  // fall into shadow. RN sets all four border colors individually — this is
  // NOT web-only.
  glassEdgeTop: string;
  glassEdgeLeft: string;
  glassEdgeRight: string;
  glassEdgeBottom: string;
  // Dark glass — the header/nav chrome, inverted against the light canvas.
  glassDarkFill: string;
  glassDarkEdge: string;

  // Design dictionary 2026-08-04 — semantic names (matches the landing's
  // --positive / --warning / --danger / --information). Fill/rail-safe; for
  // body text prefer their `*Text`-safe tokens (riskGreen, riskYellow Text,
  // riskRed, steelBlue) which are pre-darkened.
  positive?: string;
  warning?: string;
  danger?: string;
  information?: string;
}

// ============================================================================
// Fahali V3 — Dimensional Dark Palette
// ============================================================================
// Source: docs/from Kimi/V2/Fahali-World-Class-Engineering-Spec-v3.md §2.1
//
// Strategy: v3 tokens are introduced as canonical names. Legacy tokens
// (primaryBlue, accentBlue, surface, background, etc.) remain as ALIASES
// pointing at the v3 values so existing components keep rendering without
// any edits. Phase 2+ migrates components to canonical names, then legacy
// aliases get removed.
// ============================================================================

const v3Light = {
  // Background hierarchy — Cream paper. "Expensive paper", not a white app.
  // Rebased 2026-08-04 onto the design dictionary (--bg #F5F0E7, --bg-2
  // #ECE6DB, --surface #FFFFFF, --surface-2 #EEE8DC).
  backgroundVoid: "#ECE6DB",
  backgroundRoot: "#F5F0E7",      // calm mineral canvas
  backgroundElevated: "#ECE6DB",  // clear inset separation
  surfaceCard: "#FFFDF8",         // warm card, not clinical white
  surfaceFloating: "#FFFFFF",
  surfaceDeep: "#EEE8DC",
  surfaceGlow: "rgba(169,120,38,0.07)",

  // Accent — Gold. FILL/RAIL ONLY at this value (~3.7:1 on cream). See the
  // contrast contract on the Theme interface.
  primary: "#A97826",             // burnished gold fill / rail
  primaryBright: "#C19442",
  primaryGlow: "rgba(169,120,38,0.18)",
  goldText: "#8A5A12",            // AA-safe gold for text
  accentCyan: "#A97826",
  accentCyanGlow: "rgba(169,120,38,0.18)",
  accentPurple: "#5C6C82",        // repointed to Contagion Blue (kills purple)
  accentAmber: "#A97826",
  accentPink: "#943A32",

  goldSoft: "rgba(169,120,38,0.12)",
  goldLine: "rgba(169,120,38,0.30)",

  // Semantic — the founder's status set (spec item 4).
  //
  // Critical Red #943A32 replaces the deeper #943A32 and costs 5.99 -> 4.45 on
  // cream: below AA, above the founder floor. Contagion Blue #5C6C82 replaces
  // #244D82 at 8.41 -> 5.27, still clear at any size.
  //
  // Warning Gold #A97826 is the third status colour in the spec and is NOT set
  // here on purpose. `primary` is read as TEXT at 106 sites across 51 files;
  // pointing it at Yield Gold would move every one of them to 2.52:1 and ship
  // the exact defect this session spent its length removing. It is gated on
  // task #104 (migrate those sites to `goldText`), and the fill value is
  // already available as `floorPalette.amber`.
  riskRed: "#943A32",
  redFill: "#B83A31",
  bearText: "#943A32",
  riskOrange: "#9A4C10",
  riskYellow: "#8A5A12",
  riskGreen: "#176448",
  steelBlue: "#5C6C82",           // Contagion Blue — AA as text (5.27:1)
  steelSoft: "rgba(92,108,130,0.12)",
  themeYellow: "#8A5A12",

  bullSoft: "rgba(23,100,72,0.12)",
  bearSoft: "rgba(148,58,50,0.10)",
  warnSoft: "rgba(169,120,38,0.14)",

  // Hairlines derive from Ink so every rule is the same warm ink — Ink is now
  // Obsidian #171714 = rgb(28,27,25), so these move with it. A hairline left at
  // the old rgb(18,18,16) would be a second, colder ink nobody chose.
  rule: "rgba(23,23,20,0.12)",
  ruleBold: "rgba(23,23,20,0.20)",
  hair: "rgba(23,23,20,0.12)",

  inkOnGold: "#171714",

  // Text hierarchy — the founder's stated spec, measured on Cream (#F5F0E7).
  //
  // The spec names THREE tiers; this object has four. `textTertiary` and
  // `textMuted` both sit below body and both carry meta content (timestamps,
  // tags, captions), so both take Silver. Inventing a fourth value to preserve
  // a distinction the spec does not make would be re-narrowing the brief —
  // which is the error that cost two rounds on 2026-08-15.
  //
  // ⚠️ This is a token COLLAPSE, and one just caused a P0 (StoryStep painted a
  // card and its text with the same token → 21 nodes at 1:1). It is safe HERE
  // and was not there, for a reason worth stating: these two are adjacent
  // members of the SAME role (meta text). The StoryStep collapse merged a
  // SURFACE role into a TEXT role. Merging peers loses a distinction; merging
  // roles destroys contrast.
  textPrimary: "#171714",
  textSecondary: "#504B43",
  textTertiary: "#6B6459",
  textMuted: "#6B6459",
  silverFill: "#A8A49A",          // --mute-2 — rules/dots/icons, NEVER text

  // Light glass — frosted, harshly edge-lit from directly overhead.
  glassFill1: "rgba(255,253,248,0.96)",
  glassFill2: "rgba(255,253,248,0.88)",
  glassEdgeTop: "rgba(255,255,255,0.95)",    // direct overhead light
  glassEdgeLeft: "rgba(255,255,255,0.80)",
  glassEdgeRight: "rgba(255,255,255,0.30)",  // falling into shadow
  glassEdgeBottom: "rgba(255,255,255,0.20)",
  glassDarkFill: "rgba(23,23,20,0.94)",
  glassDarkEdge: "rgba(255,255,255,0.06)",
};

// Obsidian night. The same institutional world after hours — NOT an inversion.
// The brief specifies a single light system; this is the derived counterpart so
// the existing theme toggle keeps working. Obsidian is the ground here rather
// than the ink, and the colors that failed as text on sand (gold, red) pass
// comfortably on Obsidian, so they need no darkened sibling — the `*Text`
// tokens simply point at the brighter value.
const v3Dark = {
  // Navy instrument (2026-10-02) — same canvas as client/screens/V7/v7tokens.ts.
  backgroundVoid: "#070A12",
  backgroundRoot: "#0B0F1A",
  backgroundElevated: "#131A2A",
  surfaceCard: "#131A2A",
  surfaceFloating: "#1A2236",
  surfaceDeep: "#080B14",
  surfaceGlow: "rgba(217,174,95,0.10)",

  primary: "#D9AE5F",             // Yield Gold lifted for an Obsidian ground
  primaryBright: "#E8C689",
  primaryGlow: "rgba(217,174,95,0.24)",
  goldText: "#D9AE5F",            // passes on Obsidian — no darkening needed
  accentCyan: "#D9AE5F",
  accentCyanGlow: "rgba(217,174,95,0.24)",
  accentPurple: "#8296AE",        // Steel Blue lifted
  accentAmber: "#D9AE5F",
  accentPink: "#EC8C80",

  goldSoft: "rgba(217,174,95,0.14)",
  goldLine: "rgba(217,174,95,0.30)",

  riskRed: "#EC8C80",
  redFill: "#B83A31",
  bearText: "#EC8C80",
  riskOrange: "#E08A4A",
  riskYellow: "#D9AE5F",
  riskGreen: "#6FA88C",
  steelBlue: "#8296AE",           // Steel Blue lifted
  steelSoft: "rgba(130,150,174,0.14)",
  themeYellow: "#D9AE5F",

  bullSoft: "rgba(94,155,126,0.16)",
  bearSoft: "rgba(224,104,90,0.14)",
  warnSoft: "rgba(217,174,95,0.14)",

  rule: "rgba(234,238,246,0.10)",
  ruleBold: "rgba(234,238,246,0.18)",
  hair: "rgba(234,238,246,0.10)",

  inkOnGold: "#0B0F1A",

  textPrimary: "#F2EEE6",
  textSecondary: "#C9CFDB",
  textTertiary: "#AEB6C6",
  textMuted: "#97A1B5",
  silverFill: "#6B6459",

  // Dark glass — on Obsidian the light source is weaker and the edges are
  // white at low alpha rather than near-opaque.
  // Same reasoning as light: at 0.07/0.03 the pane was indistinguishable
  // from the Obsidian ground on a phone.
  // Navy-tinted glass (2026-10-02): white-alpha frost read as GREY on navy.
  glassFill1: "rgba(44,58,92,0.62)",
  glassFill2: "rgba(19,26,42,0.88)",
  glassEdgeTop: "rgba(255,255,255,0.16)",
  glassEdgeLeft: "rgba(255,255,255,0.10)",
  glassEdgeRight: "rgba(255,255,255,0.05)",
  glassEdgeBottom: "rgba(255,255,255,0.03)",
  glassDarkFill: "rgba(11,15,26,0.94)",
  glassDarkEdge: "rgba(255,255,255,0.06)",
};

export const Colors = {
  light: {
    ...v3Light,

    // Legacy aliases — DO NOT REMOVE until Phase 2+ migrates consumers.
    text: v3Light.textPrimary,
    buttonText: v3Light.inkOnGold,   // DS --ink-on-gold
    tabIconDefault: v3Light.textSecondary,
    tabIconSelected: v3Light.primary,
    link: v3Light.goldText,
    backgroundDefault: v3Light.backgroundRoot,
    backgroundSecondary: v3Light.backgroundElevated,
    backgroundTertiary: v3Light.surfaceFloating,
    border: v3Light.hair,            // DS --hair (was hardcoded 0.12)
    primaryBlue: v3Light.primary,
    accentBlue: v3Light.primaryBright,
    background: v3Light.backgroundRoot,
    surface: v3Light.surfaceCard,

    // Parity with dark. These are optional on the Theme interface, so their
    // absence typechecked — but a component reading `theme.borderActive` in
    // light mode got `undefined` and fell back to a transparent border.
    accentElectric: v3Light.primary,
    accentNeon: v3Light.riskGreen,
    glowCritical: "rgba(148,58,50,0.20)",
    glowWarning: "rgba(169,120,38,0.20)",
    glowCyan: v3Light.primaryGlow,
    glowGreen: "rgba(23,100,72,0.18)",
    surfaceElevated: v3Light.surfaceFloating,
    surfaceOverlay: "rgba(23,23,20,0.04)",
    borderActive: "rgba(169,120,38,0.34)",
    borderCritical: "rgba(148,58,50,0.30)",
    borderWarning: "rgba(169,120,38,0.28)",
    focusBorder: "rgba(169,120,38,0.55)",
    textDisabled: v3Light.textMuted,

    // Design dictionary semantics (2026-08-04) — exact token values.
    positive: v3Light.riskGreen,   // #176448
    warning: v3Light.goldText,
    danger: v3Light.riskRed,       // #943A32
    information: v3Light.steelBlue,// #244D82
  },
  dark: {
    ...v3Dark,

    // Legacy aliases.
    text: v3Dark.textPrimary,
    textDisabled: v3Dark.textMuted,
    buttonText: v3Dark.inkOnGold,                 // DS --ink-on-gold
    tabIconDefault: v3Dark.textTertiary,
    tabIconSelected: v3Dark.primary,             // gold active tab
    link: v3Dark.goldText,
    backgroundDefault: v3Dark.backgroundRoot,
    backgroundSecondary: v3Dark.backgroundElevated,
    backgroundTertiary: v3Dark.surfaceFloating,
    border: v3Dark.hair,
    primaryBlue: v3Dark.primary,
    accentBlue: v3Dark.primary,                  // gold
    accentElectric: v3Dark.primary,
    accentNeon: v3Dark.riskGreen,
    glowCritical: "rgba(224,104,90,0.25)",
    glowWarning: "rgba(217,174,95,0.20)",
    glowCyan: v3Dark.primaryGlow,                 // gold glow
    glowGreen: "rgba(94,155,126,0.18)",
    surfaceElevated: v3Dark.surfaceFloating,
    surfaceOverlay: "rgba(255, 255, 255, 0.04)",
    background: v3Dark.backgroundRoot,
    surface: v3Dark.surfaceCard,
    borderActive: "rgba(217,174,95,0.28)",
    borderCritical: "rgba(224,104,90,0.30)",
    borderWarning: "rgba(217,174,95,0.25)",
    focusBorder: "rgba(217,174,95,0.50)",

    // Design dictionary semantics — dark mode maps onto its own safe tokens.
    positive: v3Dark.riskGreen,
    warning: v3Dark.riskYellow,
    danger: v3Dark.riskRed,
    information: v3Dark.steelBlue,
  },
};

/**
 * One action language for every product surface. Fill and text colours are
 * deliberately separate so a badge can never leak an unreadable rail colour
 * into prose. Missing data is CHECKING, never a market WATCH judgment.
 */
export const ActionTokens = {
  light: {
    HOLD: { fill: "#176448", onFill: "#FAF8F2", text: "#176448", soft: "rgba(23,100,72,0.10)" },
    WATCH: { fill: "#A97826", onFill: "#171714", text: "#8A5A12", soft: "rgba(169,120,38,0.14)" },
    REDUCE: { fill: "#B83A31", onFill: "#FAF8F2", text: "#943A32", soft: "rgba(148,58,50,0.10)" },
    EXIT: { fill: "#7A211D", onFill: "#FAF8F2", text: "#7A211D", soft: "rgba(122,33,29,0.12)" },
    CHECKING: { fill: "#5C6C82", onFill: "#FAF8F2", text: "#4E6078", soft: "rgba(92,108,130,0.12)" },
  },
  dark: {
    HOLD: { fill: "#176448", onFill: "#FAF8F2", text: "#6FA88C", soft: "rgba(111,168,140,0.14)" },
    WATCH: { fill: "#A97826", onFill: "#171714", text: "#D9AE5F", soft: "rgba(217,174,95,0.14)" },
    REDUCE: { fill: "#B83A31", onFill: "#FAF8F2", text: "#EC8C80", soft: "rgba(236,140,128,0.14)" },
    EXIT: { fill: "#7A211D", onFill: "#FAF8F2", text: "#FF9B91", soft: "rgba(255,155,145,0.12)" },
    CHECKING: { fill: "#5C6C82", onFill: "#FAF8F2", text: "#8296AE", soft: "rgba(130,150,174,0.14)" },
  },
} as const;

export type ActionState = keyof typeof ActionTokens.light;

export function actionTokens(isDark: boolean, action: ActionState) {
  return ActionTokens[isDark ? "dark" : "light"][action];
}

// ============================================================================
// Spacing — v3 spec §2.3 (8px base, expanded scale)
// ============================================================================

export const Spacing = {
  // Spec scale (numeric tokens) — preferred for new work.
  1: 4,
  1.5: 6,
  2: 8,
  2.5: 10,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
  24: 96,

  // Legacy named tokens — kept for the component layer until Phase 2+.
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  "2xl": 24,
  "3xl": 32,
  "4xl": 40,
  "5xl": 48,
  inputHeight: 48,
  buttonHeight: 52,
};

// ============================================================================
// Elevation / Shadows — v3 spec §2.3
// ============================================================================

// Park Avenue elevation. The brief stacks three shadow layers (two drops and
// an inset highlight) which is a web-only capability — RN accepts exactly one
// shadow per view and no inset at all. These are the single-layer reductions
// tuned to read the same; `Glass.shadow` in constants/glass.ts carries the full
// three-layer string for web, where it genuinely renders.
//
// Shadow color is Obsidian rather than pure black: a black shadow on Warm Sand
// goes grey-violet and reads cheap.
export const Shadows = {
  cardDark: {
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.45,
    shadowRadius: 28,
    elevation: 6,
  },
  cardLight: {
    shadowColor: "#171714",
    shadowOffset: { width: 0, height: 20 },
    shadowOpacity: 0.12,
    shadowRadius: 40,
    elevation: 5,
  },
  // The deep float used by hero/action cards (brief: 0 30px 60px -12px).
  floatLight: {
    shadowColor: "#171714",
    shadowOffset: { width: 0, height: 30 },
    shadowOpacity: 0.14,
    shadowRadius: 52,
    elevation: 10,
  },
  floatDark: {
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 30 },
    shadowOpacity: 0.55,
    shadowRadius: 52,
    elevation: 10,
  },
  // Depressed state — the card sits INTO the page (brief: 0 5px 15px -10px).
  pressed: {
    shadowColor: "#171714",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.10,
    shadowRadius: 8,
    elevation: 1,
  },
  xs: {
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 2,
    elevation: 1,
  },
  sm: {
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.30,
    shadowRadius: 2,
    elevation: 2,
  },
  md: {
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.40,
    shadowRadius: 16,
    elevation: 4,
  },
  lg: {
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.50,
    shadowRadius: 32,
    elevation: 6,
  },
  xl: {
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 16 },
    shadowOpacity: 0.60,
    shadowRadius: 48,
    elevation: 8,
  },
};

export const GlowShadows = {
  primary: {
    shadowColor: v3Dark.primary,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.45,
    shadowRadius: 20,
    elevation: 8,
  },
  cyan: {
    shadowColor: v3Dark.accentCyan,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.40,
    shadowRadius: 12,
    elevation: 8,
  },
  critical: {
    shadowColor: v3Dark.riskRed,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.30,
    shadowRadius: 12,
    elevation: 6,
  },
  warning: {
    shadowColor: v3Dark.accentAmber,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.30,
    shadowRadius: 10,
    elevation: 6,
  },
};

export const RiskGradients = {
  low: [v3Light.riskGreen, "#33553F"],
  moderate: [v3Light.riskYellow, "#A87D2E"],
  elevated: [v3Light.riskOrange, "#A6350A"],
  critical: [v3Light.riskRed, "#A82C21"],
  bar: [v3Light.riskGreen, v3Light.riskYellow, v3Light.riskOrange, v3Light.riskRed],
};

// Categorical series palette. Anchored on the four brief accents, then extended
// with muted siblings that hold up on BOTH Warm Sand and Obsidian — a series
// color that only works on one ground breaks the moment the user flips theme.
export const CategoricalWarm = [
  "#A97826", // Yield Gold
  "#5C6C82", // Steel Blue
  "#176448", // Terminal Green
  "#943A32", // Alert Red
  "#8A6E3A", // bronze
  "#6E8C86", // sage teal
  "#9B6F92", // dusty plum
  "#C2410C", // clay
  "#7D8CA6", // pale steel
  "#5E7A6B", // muted moss
  "#B57A5A", // terracotta
  "#8C93B0", // periwinkle
];

export const BorderRadius = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  card: 28,    // Premium outer cards; nested controls continue to use lg/md.
  xl: 20,
  "2xl": 24,
  sheet: 26,   // DS: sheets, modals
  // Park Avenue outer-card radius — matches modern iOS widget geometry. The
  // brief's rounded-[28px]. Inner/nested elements stay smaller so the nesting
  // reads as nesting.
  outer: 28,
  "3xl": 32,
  "4xl": 40,
  full: 9999,
};

// ============================================================================
// Typography — Legacy (Outfit + Inter) and v3 spec scale
// ============================================================================
// Phase 4 will add Space Grotesk and Instrument Serif. Today we ship Outfit
// + Inter and treat them as the v3 stack stand-ins for display + body.
// ============================================================================

// Fahali Mobile Design System type scale:
//   Fraunces (italic 300) = display / brand moments ONLY · Inter = workhorse headings + body
//   JetBrains Mono = data/metrics (tabular numerals) · 12px type floor — nothing whispers.
export const Typography = {
  display: {
    fontFamily: "Fraunces_300Light_Italic",
    fontSize: 34,
    lineHeight: 40,
    letterSpacing: -0.6,
  },
  h1: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -0.6,
  },
  h2: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: -0.3,
  },
  h3: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 18,
    lineHeight: 26,
    letterSpacing: -0.2,
  },
  h4: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 17,
    lineHeight: 24,
    letterSpacing: -0.1,
  },
  body: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    lineHeight: 25,
    letterSpacing: 0,
  },
  small: {
    fontFamily: "Inter_400Regular",
    fontSize: 15,
    lineHeight: 23,
    letterSpacing: 0.1,
  },
  caption: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 13,
    lineHeight: 18,
    letterSpacing: 0.4,
  },
  link: {
    fontFamily: "Inter_500Medium",
    fontSize: 16,
    lineHeight: 24,
    letterSpacing: 0.2,
  },
  // Data voice — prices, tickers, metrics. JetBrains Mono, tabular numerals.
  tabular: {
    fontFamily: "JetBrainsMono_500Medium",
    fontSize: 16,
    lineHeight: 24,
    fontVariant: ["tabular-nums" as FontVariant],
    letterSpacing: 0,
  },
};

export const TypographyV3 = {
  display: { fontFamily: "Fraunces_400Regular", fontSize: 46, lineHeight: 52, letterSpacing: -0.92 },
  h1: { fontFamily: "Inter_600SemiBold", fontSize: 30, lineHeight: 34, letterSpacing: -0.6 },
  h2: { fontFamily: "Inter_600SemiBold", fontSize: 22, lineHeight: 26, letterSpacing: -0.3 },
  h3: { fontFamily: "Inter_600SemiBold", fontSize: 18, lineHeight: 26, letterSpacing: 0 },
  body: { fontFamily: "Inter_400Regular", fontSize: 16, lineHeight: 25, letterSpacing: -0.18 },
  bodyMedium: { fontFamily: "Inter_500Medium", fontSize: 16, lineHeight: 25, letterSpacing: -0.18 },
  caption: { fontFamily: "Inter_500Medium", fontSize: 13, lineHeight: 18, letterSpacing: 0 },
  small: { fontFamily: "Inter_600SemiBold", fontSize: 12, lineHeight: 16, letterSpacing: 0.24 },
  // 12px type floor (DS Principle 02) — was 10px. Mono micro-labels.
  micro: { fontFamily: "JetBrainsMono_500Medium", fontSize: 12, lineHeight: 14, letterSpacing: 0.4 },
  price: {
    fontFamily: "JetBrainsMono_600SemiBold",
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.5,
    fontVariant: ["tabular-nums" as FontVariant],
  },
  priceHero: {
    fontFamily: "JetBrainsMono_600SemiBold",
    fontSize: 42,
    lineHeight: 50,
    letterSpacing: -0.8,
    fontVariant: ["tabular-nums" as FontVariant],
  },
};

// ============================================================================
// Motion — v3 spec §3.2
// ============================================================================

export { Motion } from "./motionValues";

// ============================================================================
// AppFonts
// ============================================================================

// Park Avenue type stacks.
//
// `editorial` carries authority: H1s, verdicts, the large figures.
// `sans` carries data: labels, confidence scores, timestamps.
//
// 🔴 The web stacks below LEAD with the brand family and fall back to the OS
// stack, and that ordering is load-bearing. It used to be the other way
// round, defended by a comment claiming the stacks were "OS-native by design:
// zero network fetch". The consequence, measured on production 2026-08-14:
//   - `sans` never named Inter AT ALL, so 74 text nodes — every piece of body
//     copy, every caption, every section label routed through ThemedText —
//     rendered in -apple-system/Segoe UI.
//   - `mono` named "JetBrains Mono", a family nothing registered (the shipped
//     face is JetBrainsMono_500Medium), so prices fell to Consolas.
// Only `editorial` named a real registered family, which is why the display
// serif was the one voice that looked designed.
//
// The "zero external fetch" argument is answered in scripts/copy-web-build.js:
// variable Inter is copied into Fahali's own artifact, preloaded from <head>,
// and exposed through --font-inter before React runs. Nothing here reinstates
// Font.loadAsync — the static shell owns the first-paint font registration.
//
// The bare family names ("Inter", "JetBrains Mono") are ALIAS families
// declared per-weight in that build step, so fontWeight 600 resolves to the
// real SemiBold file instead of a synthetic bold smeared from Regular.
export const AppFonts = Platform.select({
  ios: {
    sans: "system-ui",
    serif: "ui-serif",
    editorial: "Fraunces_300Light_Italic",
    rounded: "ui-rounded",
    mono: "ui-monospace",
  },
  default: {
    sans: "normal",
    serif: "serif",
    editorial: "Fraunces_300Light_Italic",
    rounded: "normal",
    mono: "monospace",
  },
  web: {
    sans: "var(--font-inter), 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    serif: "'Fraunces_400Regular', Georgia, 'Times New Roman', serif",
    editorial:
      "'Fraunces_300Light_Italic', 'Iowan Old Style', 'Apple Garamond', Baskerville, 'Times New Roman', 'Droid Serif', Times, 'Source Serif Pro', serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
    rounded: "'SF Pro Rounded', 'Hiragino Maru Gothic ProN', Meiryo, 'MS PGothic', sans-serif",
    mono: "'JetBrains Mono', SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
});

export const RiskColors = {
  low: v3Light.riskGreen,
  moderate: v3Light.riskYellow,
  high: v3Light.riskOrange,
  critical: v3Light.riskRed,
};

// Foreground is chosen per-fill for contrast, not by theme: Warm Sand on the
// dark fills (red/green/steel), Obsidian on the light one (gold). Gold with
// white text is 1.9:1 — unreadable, and it was the previous value for
// `standard`.
export const AlertPriority = {
  critical: { bg: v3Light.riskRed, text: "#FAF8F2", label: "CRITICAL" },
  high: { bg: v3Light.riskOrange, text: "#FAF8F2", label: "HIGH" },
  notable: { bg: v3Light.primary, text: "#171714", label: "NOTABLE" },
  moderate: { bg: v3Light.primary, text: "#171714", label: "MODERATE" },
  standard: { bg: v3Light.steelBlue, text: "#FAF8F2", label: "STANDARD" },
  monitor: { bg: v3Light.riskGreen, text: "#FAF8F2", label: "MONITOR" },
  low: { bg: v3Light.riskGreen, text: "#FAF8F2", label: "LOW" },
};

// ============================================================================
// Tracking — the tactical meta-label voice
// ============================================================================
// The brief's `tracking-[0.2em]` on 10-11px uppercase labels. RN letterSpacing
// is in POINTS, not em, so it must be multiplied by the font size at the call
// site — 0.2em on 11px is 2.2, not 0.2. Getting this wrong is invisible in
// review and produces labels that look almost-but-not-quite right.
export const Tracking = {
  /** 0.2em — dashboard meta-labels. Pass the font size. */
  tactical: (fontSize: number) => fontSize * 0.2,
  /** 0.08em — section headings. */
  wide: (fontSize: number) => fontSize * 0.08,
};
